import React, { ChangeEvent, FC, useCallback, useEffect, useState } from 'react'
import styles from 'styles/components/template.module.scss'
import { State } from 'index'
import { useDispatch, useSelector } from 'react-redux'
import { push } from 'connected-react-router'
import { GreyButton } from 'components/Button'
import { Card, CardInner, CautionCard, CouponUserCard } from 'components/Card'
import { SearchForm } from 'components/Form'
import { CouponUserList } from 'components/List'
import { Pagination } from 'components/Pagination'
import { MainHeadline } from 'components/Typography'
import { Spacer } from 'components/Utility'
import { useSearchLinkingUser } from 'hooks/coupon'
import { PER_PAGE } from 'constants/coupon'
import { fetchCoupon, fetchCouponUsers } from 'repos/coupon'
import { CouponType } from 'types/coupon'
import { CouponUserType } from 'types/coupon_user'

type Props = {
  couponId: string | undefined
}

const CouponUsersTemplate: FC<Props> = (props) => {
  const { couponId } = props
  const dispatch = useDispatch()
  const query = useSelector((state: State) => state.router.location.query)
  const currentPage = Number(query?.page ?? '1')

  const [coupon, setCoupon] = useState<CouponType>()
  const [couponUsers, setCouponUsers] = useState<CouponUserType[]>([])
  const [matchedCouponUsers, setMatchedCouponUsers] = useState<CouponUserType[]>([])
  const [isSearchResult, setIsSearchResult] = useState<boolean>()
  const [keyword, setKeyword] = useState<string>('')

  const { matchedUsers, searchUser } = useSearchLinkingUser(keyword)

  const totalCouponUsers = couponUsers.length ?? 0

  const startIndex = (currentPage - 1) * PER_PAGE
  const endIndex = Math.min(startIndex + PER_PAGE, totalCouponUsers)
  const CouponUsers = couponUsers.slice(startIndex, endIndex)

  const goToCouponListPage = useCallback(() => dispatch(push('/admin/coupon/list')), [dispatch])

  useEffect(() => {
    ;(async () => {
      if (!couponId) return
      const coupon = await fetchCoupon(couponId)
      setCoupon(coupon)
    })()
  }, [couponId])

  useEffect(() => {
    ;(async () => {
      if (!couponId) return
      const couponUsers = await fetchCouponUsers(couponId)
      setCouponUsers(couponUsers)
    })()
  }, [couponId])

  const handleKeyword = (e: ChangeEvent<HTMLInputElement>) => {
    setKeyword(e.target.value)
    setMatchedCouponUsers([])
    setIsSearchResult(false)
  }

  useEffect(() => {
    const matchedCouponUsers = matchedUsers.flatMap((matchedUser) =>
      couponUsers.filter((couponUser) => couponUser.status === 'unused' && couponUser.user_id === matchedUser.user_id)
    )
    setMatchedCouponUsers(matchedCouponUsers)
  }, [matchedUsers, couponUsers])

  return (
    <div className={styles.standard}>
      <Spacer size={28} />
      <MainHeadline headline={`${coupon?.name}`} />
      <Spacer size={20} />
      <SearchForm
        isFilled={keyword !== ''}
        onChange={handleKeyword}
        onClick={() => {
          searchUser()
          setIsSearchResult(true)
        }}
        placeholder={'ユーザー名、電話番号、ユーザーIDで検索'}
        value={keyword}
      />
      <Spacer size={20} />
      {isSearchResult ? (
        <>
          {matchedCouponUsers.length > 0 ? (
            <Card>
              <CardInner>
                {matchedCouponUsers.map((matchedCouponUser) => (
                  <React.Fragment key={matchedCouponUser.user_id}>
                    <CouponUserCard
                      couponId={couponId}
                      couponUsers={[matchedCouponUser]}
                      uid={matchedCouponUser.user_id || ''}
                    />
                  </React.Fragment>
                ))}
              </CardInner>
            </Card>
          ) : (
            <CautionCard caution={'ユーザーが存在しません'} size={'small'} />
          )}
          <Spacer size={20} />
        </>
      ) : (
        <>
          <CouponUserList couponUsers={CouponUsers} />
          <Spacer size={24} />
          <Pagination perPage={PER_PAGE} total={totalCouponUsers} />
          <Spacer size={20} />
        </>
      )}
      <GreyButton label={'クーポン一覧に戻る'} onClick={goToCouponListPage} size={'medium'} type={'outlined'} />
      <Spacer size={48} />
    </div>
  )
}

export default CouponUsersTemplate
